import { useEffect, useMemo, useRef, useState } from 'react';
import { api, toUserFacingError } from '../../api';
import { formatMoney, formatUsd, formatUsdWithTry, usdToTryAmount } from '../../utils/currency';
import { PayurusReturnPanel } from './ActivePayurusPanel';

const STATUS_POLL_MS = 4000;

function qrUrl(data) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(data || '')}`;
}

function outcomeFromStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'approved') return 'completed';
  if (s === 'cancelled' || s === 'rejected' || s === 'failed') return 'failed';
  if (s === 'expired') return 'expired';
  return null;
}

async function copyText(text, onDone) {
  try {
    await navigator.clipboard.writeText(text);
    onDone?.(true);
    window.setTimeout(() => onDone?.(false), 1500);
  } catch {}
}

function ReceivedIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" width="56" height="56" aria-hidden="true">
      <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.35" />
      <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.12" />
      <path
        d="M20.5 32.5l7.2 7.2 15.8-16.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatDepositAmount(request) {
  if (request?.amount_usd != null && Number.isFinite(Number(request.amount_usd))) {
    return formatUsdWithTry(request.amount_usd, request.rate_usd_try);
  }
  return formatMoney(request?.amount);
}

export function ActiveCryptoPanel({
  request,
  tr: translateProp,
  t: translateLegacy,
  onGoRequests,
  variant = 'desktop',
  token,
  onUpdated,
  onOutcome,
  onContinue,
}) {
  const tr = typeof translateProp === 'function' ? translateProp : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  const [copied, setCopied] = useState(false);
  const [txHash, setTxHash] = useState(request?.tx_hash || '');
  const [savingHash, setSavingHash] = useState(false);
  const [hashError, setHashError] = useState('');
  const [liveRequest, setLiveRequest] = useState(request);
  const [phase, setPhase] = useState(() => outcomeFromStatus(request?.status));
  const checkingRef = useRef(false);
  const outcomeNotified = useRef(false);

  useEffect(() => {
    setLiveRequest(request);
    setTxHash(request?.tx_hash || '');
    const nextPhase = outcomeFromStatus(request?.status);
    setPhase(nextPhase);
    outcomeNotified.current = Boolean(nextPhase);
  }, [
    request?.id,
    request?.status,
    request?.tx_hash,
    request?.confirmations,
    request?.required_confirmations,
    request?.address,
  ]);

  const applyStatus = (next) => {
    if (!next) return;
    setLiveRequest((prev) => ({
      ...(prev || {}),
      ...next,
      address: next.address || prev?.address || null,
      coin: next.coin || prev?.coin || null,
      network: next.network || prev?.network || null,
      memo: next.memo || prev?.memo || null,
      amount_usd: next.amount_usd != null ? next.amount_usd : prev?.amount_usd,
      rate_usd_try: next.rate_usd_try != null ? next.rate_usd_try : prev?.rate_usd_try,
      estimated_crypto: next.estimated_crypto != null ? next.estimated_crypto : prev?.estimated_crypto,
      confirmations: next.confirmations != null ? next.confirmations : prev?.confirmations,
      required_confirmations:
        next.required_confirmations != null ? next.required_confirmations : prev?.required_confirmations,
      tx_hash: next.tx_hash || prev?.tx_hash || null,
    }));
    if (next.tx_hash) setTxHash(next.tx_hash);
    const nextPhase = outcomeFromStatus(next.status);
    if (!nextPhase) {
      onUpdated?.(next);
      return;
    }
    setPhase(nextPhase);
    if (!outcomeNotified.current) {
      outcomeNotified.current = true;
      onOutcome?.(nextPhase, next);
    }
  };

  const checkStatus = async () => {
    if (!token || !request?.id || checkingRef.current || phase || request?.type === 'withdraw') return;
    checkingRef.current = true;
    try {
      const data = await api.cashierRequest(token, request.id);
      applyStatus(data.request || null);
    } catch {
    } finally {
      checkingRef.current = false;
    }
  };

  useEffect(() => {
    if (!token || !request?.id || phase || request?.type === 'withdraw') return undefined;
    checkStatus();
    const id = window.setInterval(checkStatus, STATUS_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, request?.id, phase, request?.type]);

  if (!request && !liveRequest) return null;

  const current = liveRequest || request || {};
  const address = current.address || '';
  const receivedClass = variant === 'mobile'
    ? 'mobile-account-cashier__havale-received'
    : 'payments-havale-active__received';
  const receivedIconClass = variant === 'mobile'
    ? 'mobile-account-cashier__havale-received-icon'
    : 'payments-havale-active__received-icon';
  const btnClass = variant === 'mobile'
    ? 'mobile-account-cashier__notice-btn'
    : 'payments-form__quick-btn';

  const rootClass = variant === 'mobile' ? 'mobile-account-cashier__havale-active' : 'payments-crypto-active';
  const conf = current.confirmations != null ? Number(current.confirmations) : null;
  const required = current.required_confirmations != null ? Number(current.required_confirmations) : null;

  const submitTxHash = async (event) => {
    event?.preventDefault?.();
    if (!token || !request?.id || !txHash.trim()) return;
    setSavingHash(true);
    setHashError('');
    try {
      const res = await api.cryptoAttachTxHash(token, request.id, txHash.trim());
      applyStatus(res.request || res);
    } catch (err) {
      setHashError(toUserFacingError(err?.message, tr('account.payments.crypto.txHashError')));
    } finally {
      setSavingHash(false);
    }
  };

  if (phase && current.type !== 'withdraw') {
    return (
      <PayurusReturnPanel
        phase={phase}
        request={current}
        tr={tr}
        variant={variant}
        provider="direct_crypto"
        onContinue={() => {
          onContinue?.();
        }}
        onGoRequests={onGoRequests}
      />
    );
  }

  if (current.type === 'withdraw') {
    return (
      <div className={rootClass}>
        <div className={receivedClass}>
          <ReceivedIcon className={receivedIconClass} />
          <strong>{tr('account.payments.havale.withdrawReceived')}</strong>
          <p>{tr('account.payments.havale.withdrawReceivedHint', { amount: formatMoney(current.amount) })}</p>
          {address ? (
            <div className="payments-crypto-active__address-wrap">
              <span>{tr('account.payments.crypto.yourAddress')}</span>
              <code className="payments-crypto-active__address">{address}</code>
              <button
                type="button"
                className={`${btnClass} is-primary payments-crypto-active__copy-btn`}
                onClick={() => copyText(address, setCopied)}
              >
                {copied ? tr('account.payments.havale.copied') : tr('account.payments.crypto.copyAddress')}
              </button>
            </div>
          ) : null}
          <button type="button" className={`${btnClass} is-primary`} onClick={onGoRequests}>
            {tr('account.payments.goToRequests')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={variant === 'mobile' ? 'mobile-account-cashier__crypto-active' : 'payments-crypto-active'}>
      <p className="payments-form-panel__notice">{tr('account.payments.crypto.activeNotice')}</p>
      <div className="payments-crypto-active__row">
        <span>{tr('account.payments.crypto.coin')}</span>
        <strong>
          {current.coin}
          {current.network ? ` · ${current.network}` : ''}
        </strong>
      </div>
      <div className="payments-crypto-active__row">
        <span>{tr('account.payments.havale.amountDue')}</span>
        <strong>{formatDepositAmount(current)}</strong>
      </div>
      {current.estimated_crypto != null && (
        <div className="payments-crypto-active__row">
          <span>{tr('account.payments.crypto.estimated')}</span>
          <strong>
            ≈ {current.estimated_crypto} {current.coin}
          </strong>
        </div>
      )}
      {required != null && (
        <div className="payments-crypto-active__row">
          <span>{tr('account.payments.crypto.confirmations')}</span>
          <strong>
            {conf != null ? conf : 0} / {required}
          </strong>
        </div>
      )}
      {address && (
        <div className="payments-crypto-active__deposit">
          <img className="payments-crypto-active__qr" src={qrUrl(address)} alt="" />
          <div className="payments-crypto-active__address-wrap">
            <code className="payments-crypto-active__address">{address}</code>
            <button
              type="button"
              className={`${btnClass} is-primary payments-crypto-active__copy-btn`}
              onClick={() => copyText(address, setCopied)}
            >
              {copied ? tr('account.payments.havale.copied') : tr('account.payments.crypto.copyAddress')}
            </button>
          </div>
          {current.memo ? (
            <div className="payments-crypto-active__row">
              <span>Memo / Tag</span>
              <strong>{current.memo}</strong>
            </div>
          ) : null}
        </div>
      )}
      <form className="payments-crypto-active__txhash" onSubmit={submitTxHash}>
        <label className="payments-form__field">
          <span className="payments-form__label">{tr('account.payments.crypto.txHashOptional')}</span>
          <div className="payments-form__input-line">
            <input
              className="payments-form__input"
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
              placeholder="0x… / TxID"
              autoComplete="off"
            />
          </div>
        </label>
        {hashError ? <p className="payments-form__error">{hashError}</p> : null}
        <button type="submit" className={`${btnClass} is-primary`} disabled={savingHash || !txHash.trim()}>
          {savingHash ? tr('account.loading') : tr('account.payments.crypto.submitTxHash')}
        </button>
      </form>
    </div>
  );
}

export function CryptoAmountHint({ amount, usdtTry, tr: translateProp, t: translateLegacy }) {
  const tr = typeof translateProp === 'function' ? translateProp : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  const tryAmount = usdToTryAmount(amount, usdtTry);
  if (!amount || tryAmount == null) return null;
  return (
    <p className="payments-form-panel__notice payments-crypto-usd-hint">
      {tr('account.payments.crypto.usdTryHint', { value: formatUsdWithTry(amount, usdtTry) })}
    </p>
  );
}

export function CryptoAssetPicker({
  assets,
  selectedId,
  onSelect,
  tr: translateProp, t: translateLegacy,
  direction = 'deposit',
  walletAddress,
  onWalletAddressChange,
  walletMemo,
  onWalletMemoChange,
  loading = false,
  onStepChange,
  amount,
  onAmountChange,
  amountMin = 0,
  amountMax = 100000,
  quickAmounts = [],
  usdtTry = null,
  amountError = null,
  amountErrorDetail = null,
  submitLabel = null,
  onSubmitDeposit = null,
  submitLoading = false,
}) {
  const tr = typeof translateProp === 'function' ? translateProp : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState('coin');
  const [pickedCoin, setPickedCoin] = useState(null);
  const list = Array.isArray(assets) ? assets : [];
  const isDeposit = direction === 'deposit';
  const value = Number(amount) || 0;
  const min = Number(amountMin) || 0;
  const max = Number(amountMax) || 100000;
  const amountOk = value >= min && value <= max;
  const quick = (Array.isArray(quickAmounts) ? quickAmounts : []).filter((item) => item >= min && item <= max);

  const coins = useMemo(() => {
    const map = new Map();
    for (const asset of list) {
      if (!map.has(asset.coin)) {
        map.set(asset.coin, {
          coin: asset.coin,
          coinName: asset.coinName,
          iconUrl: asset.iconUrl,
        });
      }
    }
    return [...map.values()];
  }, [list]);

  const selected = list.find((a) => a.id === selectedId) || null;
  const activeCoin = pickedCoin || selected?.coin || null;
  const networks = list.filter((a) => a.coin === activeCoin);

  const assetsKey = useMemo(() => list.map((a) => a.id).join(','), [list]);

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  useEffect(() => {
    setStep('coin');
    setPickedCoin(null);
    onSelect(null);
  }, [assetsKey, loading, onSelect]);

  useEffect(() => {
    setCopied(false);
  }, [selected?.address]);

  if (loading) {
    return (
      <div className="payments-crypto-picker payments-crypto-picker--loading" role="status" aria-busy="true">
        <div className="payments-crypto-picker__loader" aria-hidden="true" />
        <p className="payments-form-panel__notice">{tr('account.loading')}</p>
      </div>
    );
  }

  if (!list.length) {
    return <p className="payments-form-panel__notice">{tr('account.payments.crypto.noAssets')}</p>;
  }

  const goBack = () => {
    if (step === 'wallet') {
      if (isDeposit) {
        setStep('amount');
        return;
      }
      setStep('network');
      onSelect(null);
      return;
    }
    if (step === 'amount') {
      setStep('network');
      onSelect(null);
      return;
    }
    if (step === 'network') {
      setStep('coin');
      setPickedCoin(null);
      onSelect(null);
    }
  };

  const goToWallet = () => {
    if (!amountOk || !selected) return;
    setStep('wallet');
  };

  return (
    <div className="payments-crypto-picker">
      {step !== 'coin' ? (
        <button type="button" className="payments-crypto-picker__back" onClick={goBack}>
          {tr('account.payments.back')}
        </button>
      ) : null}

      {step === 'coin' ? (
        <>
          <div className="payments-crypto-picker__label">{tr('account.payments.crypto.selectCoin')}</div>
          <div className="payments-crypto-picker__coins">
            {coins.map((coin) => (
              <button
                key={coin.coin}
                type="button"
                className="payments-crypto-coin"
                onClick={() => {
                  setPickedCoin(coin.coin);
                  onSelect(null);
                  setStep('network');
                }}
              >
                <img src={coin.iconUrl} alt="" width="28" height="28" />
                <span>{coin.coin}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {step === 'network' ? (
        <div className="payments-crypto-picker__network-block">
          <div className="payments-crypto-picker__label">
            {tr('account.payments.crypto.selectNetwork')}
            {activeCoin ? ` · ${activeCoin}` : ''}
          </div>
          <div className="payments-crypto-picker__networks" role="listbox" aria-label={tr('account.payments.crypto.network')}>
            {networks.map((net) => (
              <button
                key={net.id}
                type="button"
                role="option"
                aria-selected={false}
                className="payments-crypto-network"
                onClick={() => {
                  onSelect(net.id);
                  setStep(isDeposit ? 'amount' : 'wallet');
                }}
              >
                {net.network}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {step === 'amount' && selected && isDeposit ? (
        <>
          <div className="payments-crypto-network-selected" role="status">
            <span className="payments-crypto-network-selected__label">
              {tr('account.payments.crypto.selectedNetwork')}
            </span>
            <strong className="payments-crypto-network-selected__value">
              {selected.coin}
              {selected.network ? ` · ${selected.network}` : ''}
            </strong>
          </div>
          <p className="payments-crypto-network-warning">
            {tr('account.payments.crypto.networkWarning', { network: selected.network || '—' })}
          </p>
          <label className="payments-form__field">
            <span className="payments-form__label">{tr('account.cashier.amountUsd')}</span>
            <div className="payments-form__input-line">
              <input
                className="payments-form__input"
                type="number"
                min={min}
                max={max}
                step="0.01"
                value={amount || ''}
                placeholder={tr('account.payments.fromUsd', { amount: formatUsd(min) })}
                onChange={(e) => onAmountChange?.(e.target.value)}
              />
            </div>
          </label>
          <CryptoAmountHint amount={value} usdtTry={usdtTry} tr={tr} />
          {quick.length > 0 ? (
            <div className="payments-form__quick">
              {quick.map((item) => (
                <button
                  key={item}
                  type="button"
                  className="payments-form__quick-btn"
                  onClick={() => onAmountChange?.(String(item))}
                >
                  ${item}
                </button>
              ))}
            </div>
          ) : null}
          {amountError ? (
            <div className="payments-form__error">
              <strong>{amountError}</strong>
              {amountErrorDetail ? <span className="payments-form__error-detail">{amountErrorDetail}</span> : null}
            </div>
          ) : null}
          <button
            type="button"
            className="payments-form__submit"
            disabled={!amountOk}
            onClick={goToWallet}
          >
            {tr('account.payments.crypto.continueToWallet')}
          </button>
        </>
      ) : null}

      {step === 'wallet' && selected ? (
        <>
          <div className="payments-crypto-network-selected" role="status">
            <span className="payments-crypto-network-selected__label">
              {tr('account.payments.crypto.selectedNetwork')}
            </span>
            <strong className="payments-crypto-network-selected__value">
              {selected.coin}
              {selected.network ? ` · ${selected.network}` : ''}
            </strong>
          </div>
          <p className="payments-crypto-network-warning">
            {tr('account.payments.crypto.networkWarning', { network: selected.network || '—' })}
          </p>

          {isDeposit && amountOk ? (
            <div className="payments-crypto-active__row">
              <span>{tr('account.cashier.amountUsd')}</span>
              <strong>{formatUsdWithTry(value, usdtTry)}</strong>
            </div>
          ) : null}

          {isDeposit && selected.address ? (
            <div className="payments-crypto-preview">
              <img src={qrUrl(selected.address)} alt="" width="120" height="120" />
              <div className="payments-crypto-preview__body">
                <div className="payments-crypto-preview__caption">{tr('account.payments.crypto.depositAddress')}</div>
                <div className="payments-crypto-preview__addr-stack">
                  <code className="payments-crypto-preview__addr">{selected.address}</code>
                  <button
                    type="button"
                    className="payments-form__quick-btn is-primary payments-crypto-preview__copy"
                    onClick={() => copyText(selected.address, setCopied)}
                  >
                    {copied ? tr('account.payments.havale.copied') : tr('account.payments.crypto.copyAddress')}
                  </button>
                </div>
                {selected.memo ? <div className="payments-crypto-preview__memo">Memo: {selected.memo}</div> : null}
              </div>
            </div>
          ) : null}

          {isDeposit ? (
            <button
              type="button"
              className="payments-form__submit"
              disabled={submitLoading || !amountOk || !selectedId}
              onClick={() => onSubmitDeposit?.()}
            >
              {submitLabel || tr('account.payments.addAmountUsd', {
                amount: amountOk ? formatUsdWithTry(value, usdtTry) : formatUsdWithTry(min, usdtTry),
              })}
            </button>
          ) : null}

          {!isDeposit ? (
            <>
              <label className="payments-form__field">
                <span className="payments-form__label">{tr('account.payments.crypto.yourAddress')}</span>
                <div className="payments-form__input-line">
                  <input
                    className="payments-form__input"
                    value={walletAddress || ''}
                    onChange={(e) => onWalletAddressChange?.(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
              </label>
              <label className="payments-form__field">
                <span className="payments-form__label">Memo / Tag</span>
                <div className="payments-form__input-line">
                  <input
                    className="payments-form__input"
                    value={walletMemo || ''}
                    onChange={(e) => onWalletMemoChange?.(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </label>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
